import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { basePathSite } from '@/pages/assistente-evento/utils';
import { ICONE_APP } from '@/lib/app';
import Modal from '@/components/ui/Modal';
import Campo from '@/components/ui/Campo';

// ---------------------------------------------------------------------------
// "Mandar aviso no celular" — a direção escreve um aviso curto e escolhe o
// público (todos do evento, vendedores da ação entre amigos ou equipe do dia).
// Prévia: rpc aviso_previa_publico. Envio: rpc enviar_aviso_publico.
// ---------------------------------------------------------------------------
interface Props {
  eventoId: number;
  slug: string;
  /** Apoio do cartão, escrito conforme o tipo de evento. */
  texto?: string;
}

type Publico = 'todos' | 'vendedores' | 'equipe';
type LinkAviso = 'evento' | 'compras' | 'talao' | 'nenhum';

interface Previa {
  pessoas_com_avisos: number;
  aparelhos: number;
  total_no_publico: number;
}

const LIMITE_TITULO = 60;
const LIMITE_CORPO = 240;

const CARD = 'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

const PUBLICOS: { id: Publico; rotulo: string }[] = [
  { id: 'todos', rotulo: 'Todos do evento' },
  { id: 'vendedores', rotulo: 'Vendedores da ação' },
  { id: 'equipe', rotulo: 'Equipe do dia' },
];

// Texto pronto sugerido para os vendedores da ação entre amigos.
const SUGESTAO_VENDEDORES = {
  titulo: 'Bora vender a ação entre amigos!',
  corpo: 'Faltam poucos dias. Abra seu talão no Se Liga e mande seu link no WhatsApp.',
};

function palavraDoPublico(p: Publico, n: number): string {
  if (p === 'vendedores') return n === 1 ? 'vendedor' : 'vendedores';
  if (p === 'equipe') return n === 1 ? 'pessoa da equipe' : 'pessoas da equipe';
  return n === 1 ? 'pessoa' : 'pessoas';
}

export default function MandarAviso({ eventoId, slug, texto }: Props) {
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erroPrevia, setErroPrevia] = useState<string | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);

  const [publico, setPublico] = useState<Publico>('todos');
  const [titulo, setTitulo] = useState('');
  const [corpo, setCorpo] = useState('');
  const [link, setLink] = useState<LinkAviso>('nenhum');

  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<string | null>(null);

  async function carregarPrevia(p: Publico) {
    setCarregando(true);
    setErroPrevia(null);
    try {
      const { data, error } = await supabase.rpc('aviso_previa_publico', {
        p_evento: eventoId,
        p_publico: p,
      });
      if (error) throw error;
      const d = data as {
        pessoas_com_avisos?: number;
        aparelhos?: number;
        total_no_publico?: number;
      } | null;
      setPrevia({
        pessoas_com_avisos: Number(d?.pessoas_com_avisos) || 0,
        aparelhos: Number(d?.aparelhos) || 0,
        total_no_publico: Number(d?.total_no_publico) || 0,
      });
    } catch (e) {
      setErroPrevia(mensagemAmigavel(e, 'Não foi possível carregar os avisos do evento.'));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (!aberto) return;
    setPublico('todos');
    setTitulo('');
    setCorpo('');
    setLink('nenhum');
    setConfirmando(false);
    setErroEnvio(null);
    setEnviado(null);
    void carregarPrevia('todos');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, eventoId]);

  function trocarPublico(p: Publico) {
    if (p === publico) return;
    setPublico(p);
    setPrevia(null);
    setEnviado(null);
    setErroEnvio(null);
    setConfirmando(false);
    // Sugere o link e o texto pronto para os vendedores da ação entre amigos.
    if (p === 'vendedores') {
      if (!titulo.trim()) setTitulo(SUGESTAO_VENDEDORES.titulo);
      if (!corpo.trim()) setCorpo(SUGESTAO_VENDEDORES.corpo);
      setLink('talao');
    }
    void carregarPrevia(p);
  }

  function urlEscolhida(): string {
    const base = basePathSite();
    if (link === 'evento') return `${base}/e/${slug}`;
    if (link === 'compras') return `${base}/meus-ingressos`;
    if (link === 'talao') return `${base}/meu-talao`;
    return '';
  }

  function rotuloLink(): string {
    if (link === 'evento') return 'Abrir o evento';
    if (link === 'compras') return 'Ver minhas compras';
    if (link === 'talao') return 'Abrir meu talão';
    return '';
  }

  const pessoasComAvisos = previa?.pessoas_com_avisos ?? 0;
  const aparelhos = previa?.aparelhos ?? 0;
  const total = previa?.total_no_publico ?? 0;
  const semNinguem = previa !== null && pessoasComAvisos === 0;
  const tituloOk = titulo.trim().length >= 3;
  const corpoOk = corpo.trim().length >= 3;
  const podeEnviar = previa !== null && !semNinguem && tituloOk && corpoOk && !enviando;

  function pedirConfirmacao() {
    setErroEnvio(null);
    setEnviado(null);
    if (titulo.trim().length < 3 || corpo.trim().length < 3) {
      setErroEnvio('Escreva o título e a mensagem.');
      return;
    }
    setConfirmando(true);
  }

  async function enviar() {
    if (enviando) return;
    setConfirmando(false);
    setEnviando(true);
    setErroEnvio(null);
    try {
      const { data, error } = await supabase.rpc('enviar_aviso_publico', {
        p_evento: eventoId,
        p_publico: publico,
        p_titulo: titulo.trim(),
        p_corpo: corpo.trim(),
        p_url: urlEscolhida(),
      });
      if (error) {
        setErroEnvio(mensagemAmigavel(error));
        return;
      }
      const n = Number((data as { pessoas?: number } | null)?.pessoas) || 0;
      setEnviado(`Aviso enviado para ${n} ${palavraDoPublico(publico, n)}.`);
      setTitulo('');
      setCorpo('');
      await carregarPrevia(publico);
    } catch (e) {
      setErroEnvio(mensagemAmigavel(e, 'Não foi possível enviar o aviso.'));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <div className={CARD}>
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
            <i className="ri-notification-badge-line text-xl" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] font-bold text-foreground-950">Mandar aviso no celular</h2>
            <p className="mt-0.5 text-[14px] leading-relaxed text-foreground-600">
              {texto ??
                'Envie um recado curto para todos do evento, só os vendedores da ação ou a equipe do dia.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAberto(true)}
          className="mt-3 flex h-11 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-send-plane-fill" />
          </span>
          Escrever aviso
        </button>
      </div>

      <Modal aberto={aberto} titulo="Mandar aviso no celular" aoFechar={() => setAberto(false)}>
        <div className="space-y-4 pb-1">
          {/* Para quem? */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">
              Para quem?
            </label>
            <div className="flex flex-wrap gap-2">
              {PUBLICOS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => trocarPublico(p.id)}
                  className={`h-10 cursor-pointer whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors ${
                    publico === p.id
                      ? 'bg-primary-500 text-white'
                      : 'border border-secondary-300 bg-white text-foreground-700 hover:bg-background-50'
                  }`}
                >
                  {p.rotulo}
                </button>
              ))}
            </div>
          </div>

          {erroPrevia ? (
            <div className="py-1">
              <p className="text-sm text-foreground-700">{erroPrevia}</p>
              <button
                type="button"
                onClick={() => void carregarPrevia(publico)}
                className="mt-3 h-11 w-full cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Tentar de novo
              </button>
            </div>
          ) : carregando || previa === null ? (
            <div className="h-16 animate-pulse rounded-2xl bg-secondary-100" />
          ) : semNinguem ? (
            <div className="rounded-2xl bg-warning-50 p-3.5">
              <p className="text-sm font-semibold text-warning-800">
                {publico === 'vendedores'
                  ? 'Nenhum vendedor deste evento ligou os avisos ainda.'
                  : publico === 'equipe'
                    ? 'Ninguém da equipe deste evento ligou os avisos ainda.'
                    : 'Ninguém deste evento ligou os avisos ainda.'}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-foreground-700">
                Peça para as pessoas instalarem o app e tocarem em “Ativar avisos” — aí o recado
                chega no celular delas.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl bg-primary-50 p-3.5">
              <p className="text-[15px] leading-relaxed text-foreground-800">
                {publico === 'vendedores' ? (
                  <>
                    Vai chegar para{' '}
                    <strong className="font-bold">{pessoasComAvisos}</strong> de{' '}
                    <strong className="font-bold">{total}</strong> vendedores ({aparelhos}{' '}
                    {aparelhos === 1 ? 'aparelho' : 'aparelhos'}).
                  </>
                ) : (
                  <>
                    Vai chegar para{' '}
                    <strong className="font-bold">{pessoasComAvisos}</strong>{' '}
                    {palavraDoPublico(publico, pessoasComAvisos)} ({aparelhos}{' '}
                    {aparelhos === 1 ? 'aparelho' : 'aparelhos'}).
                  </>
                )}
              </p>
              {publico === 'vendedores' && pessoasComAvisos < total && (
                <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">
                  Os outros ainda não ligaram os avisos.
                </p>
              )}
            </div>
          )}

          {previa !== null && !semNinguem && !erroPrevia && (
            <>
              <Campo
                rotulo="Título"
                value={titulo}
                onChange={(v) => setTitulo(v.slice(0, LIMITE_TITULO))}
                placeholder="Ex.: As inscrições abriram!"
                maxLength={LIMITE_TITULO}
                ajuda={`${titulo.length}/${LIMITE_TITULO}`}
              />

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">
                  Mensagem
                </label>
                <textarea
                  value={corpo}
                  onChange={(e) => setCorpo(e.target.value.slice(0, LIMITE_CORPO))}
                  rows={3}
                  maxLength={LIMITE_CORPO}
                  placeholder="Escreva um recado curto e direto."
                  className="w-full resize-none rounded-xl border border-secondary-300 bg-white px-3.5 py-2.5 text-base text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                <p className="mt-1 text-right text-sm text-foreground-500">
                  {corpo.length}/{LIMITE_CORPO}
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">
                  Link do aviso
                </label>
                <select
                  value={link}
                  onChange={(e) => setLink(e.target.value as LinkAviso)}
                  className="h-12 w-full rounded-xl border border-secondary-300 bg-white px-3 text-base text-foreground-950 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <option value="nenhum">Nenhum</option>
                  <option value="evento">Página do evento</option>
                  <option value="talao">Meu talão (ação entre amigos)</option>
                  <option value="compras">Minhas compras</option>
                </select>
              </div>

              {/* Prévia no celular */}
              <div>
                <p className="mb-1.5 text-sm font-medium text-foreground-800">
                  Como aparece no celular
                </p>
                <div className="rounded-2xl bg-foreground-950/95 p-3">
                  <div className="flex items-start gap-3 rounded-xl bg-white/95 p-3">
                    <img
                      src={ICONE_APP}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-lg object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-bold text-foreground-950">
                        {titulo.trim() || 'Título do aviso'}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-foreground-600">
                        {corpo.trim() || 'A mensagem aparece aqui, curtinha.'}
                      </p>
                      {urlEscolhida() && (
                        <p className="mt-1 text-[11px] font-semibold text-primary-600">
                          {rotuloLink()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {erroEnvio && (
                <p className="text-sm text-danger-600" role="alert">
                  {erroEnvio}
                </p>
              )}
              {enviado && (
                <div className="flex items-center gap-2 rounded-2xl bg-success-50 px-3.5 py-3">
                  <span className="flex h-5 w-5 items-center justify-center text-success-600">
                    <i className="ri-checkbox-circle-line" />
                  </span>
                  <p className="text-sm font-semibold text-success-800">{enviado}</p>
                </div>
              )}

              {confirmando ? (
                <div className="rounded-2xl border border-primary-200 bg-primary-50 p-3.5">
                  <p className="text-sm font-semibold text-foreground-900">
                    Enviar agora para {pessoasComAvisos}{' '}
                    {palavraDoPublico(publico, pessoasComAvisos)}?
                  </p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={enviar}
                      disabled={enviando}
                      className="flex h-11 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-70"
                    >
                      <span className="flex h-5 w-5 items-center justify-center">
                        <i
                          className={enviando ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'}
                        />
                      </span>
                      {enviando ? 'Enviando…' : 'Confirmar envio'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmando(false)}
                      className="flex h-11 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={pedirConfirmacao}
                  disabled={!podeEnviar}
                  className="flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-6 text-base font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="flex h-5 w-5 items-center justify-center">
                    <i className="ri-send-plane-fill" />
                  </span>
                  Enviar aviso
                </button>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
}